import { useLocation } from 'react-router-dom';
import { PageShell } from '../components';
import { PageBody, PageHero, Section, FeatureRows } from '../components/page';

/**
 * The page that did not exist.
 *
 * Before this, a URL that matched no route rendered NOTHING: `public/_redirects`
 * serves `index.html` with a 200 for every path, `main.tsx` client-renders
 * `<Routes>`, and `App.tsx` had no `path="*"`. `scripts/check-links.mjs` now
 * fails the build on a broken internal href, so this should be unreachable from
 * inside the site. It exists for the paths we do not control: a stale bookmark,
 * a truncated URL in a chat client, a link from before a route was renamed.
 *
 * It is a real route in `vite-plugins/routes.ts`, so it prerenders like every
 * other page and reads correctly before any JavaScript runs.
 *
 * The destinations are the four things a lost reader actually wants, in the
 * order they want them. Not an alphabetical sitemap.
 */

const DESTINATIONS = [
  {
    title: 'Start here',
    body: 'What Statio is, and what it does about MCP access.',
    href: '/',
    linkLabel: 'Home',
  },
  {
    title: 'Documentation',
    body: 'MCP explained, shadow AI discovery, deploying from CI, outbound control.',
    href: 'https://docs.statio.dev/docs/',
    linkLabel: 'Read the docs',
  },
  {
    title: 'Getting started',
    body: 'Set up your account, the desktop app and your first governed MCP server.',
    href: '/getting-started',
    linkLabel: 'Get started',
  },
  {
    title: 'Pricing',
    body: 'Four self-serve plans at published monthly prices, plus Enterprise.',
    href: '/pricing',
    linkLabel: 'See pricing',
  },
];

export default function NotFoundPage() {
  const { pathname } = useLocation();

  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="That page doesn’t exist."
          lede={
            pathname && pathname !== '/'
              ? `Nothing is published at ${pathname}. It may have moved, or the link may be incomplete.`
              : 'Nothing is published at that address. It may have moved, or the link may be incomplete.'
          }
        />
        <Section id="onward" title="Where to go from here">
          <FeatureRows items={DESTINATIONS} />
        </Section>
      </PageBody>
    </PageShell>
  );
}
